import { LocalizationMap, ProgressBar, Viewer, Worker } from "@react-pdf-viewer/core";
import { Button } from "components/shared";
import { FC, useEffect, useState } from "react";
import StepInfo from "pages/StepPages/components";
import { ReactComponent as LoadingIcon } from "images/load.svg";
import { ReactComponent as EditorIcon } from "images/EditorIcon.svg";
import { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { ReactComponent as FileSvg } from "images/file.svg";
import Badge from "components-new/Badge";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import ru_RU from "@react-pdf-viewer/locales/lib/ru_RU.json";
import { getCommission, getRoles, uploadDocx } from "toolkit/steps/stepOne/stepOne.thunks";
import { TypeBadge } from "components-new/Badge/Badge";
import { $api, baseURL } from "libs/axios";
import { setCommission } from "toolkit/steps/stepOne/stepOne.slice";
import cn from "classnames";
import styles from "./Commission.module.sass";
import { snackWrapper } from "helpers";
import {commissionsAPI} from "libs/apiClient/Commissions";

const commissionStatus: Record<string, keyof typeof TypeBadge> = {
	"Утверждена": "success",
	"Сформирован приказ о создании": "warn",
	"Ошибка": "error",
} as const;

const Commission: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const [url, setUrl] = useState<string>("");
	const { organizationId, commissionId } = useParams();

	const { alreadyAddedEmployees, currentCommission, roles, loading } = useAppSelector((state) => state.stepOne);

	const isEndDate = currentCommission?.endDate ? (
		<>
			<span>Комиссия по категорированию объектов КИИ — </span>
			<span className={styles.date}>до {currentCommission?.endDate}</span>
		</>
	) : (
		"Комиссия по категорированию объектов КИИ"
	);

	useEffect(() => {
		if (!roles.length) {
			dispatch(getRoles());
		}

		if (commissionId) {
			dispatch(getCommission(+commissionId));
		}
		//eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (currentCommission?.newestDocument?.id) {

			const fetchUrl = async () => {
				setUrl((await commissionsAPI.docsLinkDetail(currentCommission.newestDocument.id,'pdf').then(({data})=>data)));
			};

			fetchUrl();
		}
	}, [currentCommission]);

	const exportDocumentInPdf = async () => {
		const u = await commissionsAPI.docsLinkDetail(currentCommission!.newestDocument.id,'docx').then((r) => r.data);
		$api.get(u, { method: "GET", responseType: "blob" }).then((res) => {
			const href = URL.createObjectURL(res.data);

			const link = document.createElement("a");
			link.href = href;
			link.setAttribute("download", "file.docx"); // Добавить название файла
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(href);
		});
	};

	const closeCommission = async () => {
		try {
			await commissionsAPI.closeCreate(+commissionId!);

			navigate(`/categorization/organizations/${organizationId}/steps/1.1`, {
				replace: true,
			});

			dispatch(setCommission({ commission: null, commissionId: 0 }));
			snackWrapper("success", "Комиссия успешно закрыта");
		} catch (error) {
			snackWrapper("error", "Не удалось закрыть комиссию");
		}
	};

	return (
		<div className="step-wrapper">
			<div className={cn("step-header", styles.stepHeader)}>
				<StepInfo info={isEndDate} />
				{/* <Button>Загрузить шаблон приказа</Button> */}
				<Button variant={BUTTON_VARIANTS.OUTLINED} onClick={closeCommission}>
					Удалить комиссию
				</Button>
			</div>

			<div className={styles.commission}>
				<div className={styles.left}>
					<div className={styles.list}>
						<div className={styles.listHeader}>
							<div className={styles.status}>
								<div className={styles.employeesTitle}>Участники</div>

								<Badge type={currentCommission?.status ? commissionStatus[currentCommission.status] : "info"}>
									{currentCommission?.status === "Утверждена" ? "Комиссия утверждена" : currentCommission?.status || ""}
								</Badge>
							</div>

							<Link className={styles.link} to={`/newCommission/${organizationId}/${commissionId}`} replace>
								<EditorIcon className={styles.editIcon} />
								Скорректировать состав комиссии
							</Link>
						</div>

						<div data-comment-false={true} className={styles.employees}>
							{loading ? (
								<LoadingIcon className={styles.loaderIcon} />
							) : alreadyAddedEmployees.length ? (
								alreadyAddedEmployees.map((employee) => (
									<div key={employee.id} className={styles.employee}>
										<div>
											{employee?.employeeTableEmployee?.fullName}
											{/*{employee?.employeeTableEmployee?.lastName} {employee?.employeeTableEmployee?.firstName}{" "}*/}
											{/*{employee?.employeeTableEmployee?.middleName}*/}
										</div>
										<div className={styles.name}>{employee?.commissionRoleId?.name}</div>
									</div>
								))
							) : (
								<div className={styles.empty}>В составе комисси нет ни одного сотрудника</div>
							)}
						</div>
					</div>
					<div className={styles.buttons}>
						<Button onClick={exportDocumentInPdf} full variant={BUTTON_VARIANTS.OUTLINED} className={styles.button}>
							<FileSvg className={styles.fileIcon} /> Экспорт приказа.docx
						</Button>

						<label>
							<input
								type="file"
								accept=".docx, application/pdf"
								className={styles.inputFile}
								onChange={(e: any) => {
									let ext = e.target.value.match(/\.([^.]+)$/)[1];
									switch (ext) {
										case "docx":
											dispatch(
												uploadDocx({
													docId: currentCommission?.newestDocument?.id,
													file: e.target.files[0],
													commissionId,
												}),
											);
											break;
										case "pdf":
											dispatch(
												uploadDocx({
													docId: currentCommission?.newestDocument?.id,
													file: e.target.files[0],
													commissionId,
												}),
											);
											break;
										default:
											// TODO: Для отладки при тесте
											console.log("Файл не поддерживается, только .docx");
											console.log("Загружаемый файл:", ext);
											break;
									}
								}}
							/>
							Загрузить утвержденный приказ
						</label>
					</div>
				</div>

				<div data-comment-false={true} className={styles.viewer}>
					<Worker workerUrl="/static/js/pdf.worker.min.js">
						<Viewer
							fileUrl={`${baseURL}${url ? url : "/"}`}
							httpHeaders={{
								Authorization: `Bearer ${localStorage.getItem("token")}`,
							}}
							renderLoader={(percentages: number) => (
								<div style={{ width: "240px" }}>
									<ProgressBar progress={Math.round(percentages)} />
								</div>
							)}
							renderError={() => <div></div>}
							localization={ru_RU as unknown as LocalizationMap}
						/>
					</Worker>
				</div>
			</div>
		</div>
	);
};

export default Commission;
